// src/components/modals/CreateMarcaModal.jsx
import React, { useState } from 'react';
import { FaTags, FaPalette, FaFont, FaQuoteLeft, FaSpinner, FaPlus, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import LogotipoModal from './LogotipoModal';

const RUBROS_NEGOCIOS = [
    'Restaurante/Cafetería',
    'Tienda de Ropa',
    'Salón de Belleza/Spa',
    'Gimnasio/Centro Deportivo',
    'Tienda de Alimentos',
    'Farmacia',
    'Librería/Papelería',
    'Ferretería',
    'Tecnología/Electrónica',
    'Mueblería/Decoración',
    'Joyería/Accesorios',
    'Panadería/Pastelería',
    'Veterinaria/Pet Shop',
    'Centro Médico/Clínica',
    'Autoservicio/Minimarket',
    'Otros'
];

// Definir el mapeo de fuentes con sus fallbacks
const FONT_FAMILIES = {
    'Poppins': "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    'Inter': "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Montserrat': "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Roboto': "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Open Sans': "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Lato': "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Nunito': "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'DM Sans': "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Arial': "Arial, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Helvetica': "Helvetica, Arial, -apple-system, BlinkMacSystemFont, sans-serif",
    'Verdana': "Verdana, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Tahoma': "Tahoma, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Playfair Display': "'Playfair Display', Georgia, serif",
    'Quicksand': "'Quicksand', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Comfortaa': "'Comfortaa', -apple-system, BlinkMacSystemFont, cursive",
    'Righteous': "'Righteous', -apple-system, BlinkMacSystemFont, cursive",
    'Merriweather': "'Merriweather', Georgia, serif",
    'Source Serif Pro': "'Source Serif Pro', Georgia, serif",
    'Crimson Text': "'Crimson Text', Georgia, serif",
    'Lora': "'Lora', Georgia, serif",
    'Work Sans': "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Karla': "'Karla', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Manrope': "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    'Outfit': "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
};

const CreateMarcaModal = ({ isOpen, onClose, onSubmit, onUploadLogotipo, onComplete, user }) => {
    // Estados para el formulario
    const [newMarca, setNewMarca] = useState({
        nombre: '',
        rubro: '',
        tiendas: [],
        clientes: [],
        pointscondition: 'Di tu condición para acumular puntos',
        puntos: 'PUNTOS',
        puntosTicket: 6,
        coloresCorporativos: [],
        tipografia: 'Arial', // Valor por defecto para que sea obligatorio
        eslogan: ''
    });

    // Estados para el modal
    const [newColor, setNewColor] = useState('');
    const [selectedColor, setSelectedColor] = useState('#3B82F6');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showLogoModal, setShowLogoModal] = useState(false);
    const [marcaCreada, setMarcaCreada] = useState(null);



    // Colores predefinidos para el color picker
    const predefinedColors = [
        '#EF4444', '#F97316', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899',
        '#06B6D4', '#84CC16', '#F43F5E', '#A855F7', '#14B8A6', '#FBBF24', '#22C55E'
    ];

    if (!isOpen) return null;

    // Función para validar colores
    const validateColor = (color) => {
        // Crear un elemento temporal para validar el color
        const tempElement = document.createElement('div');
        tempElement.style.color = color;

        // Si el color es válido, el navegador lo aplicará
        const isValid = tempElement.style.color !== '';

        // Validaciones adicionales
        if (!isValid) return false;

        // Validar formato hexadecimal
        const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
        if (hexRegex.test(color)) return true;

        // Validar formato RGB/RGBA
        const rgbRegex = /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/;
        if (rgbRegex.test(color)) return true;

        // Validar formato HSL/HSLA
        const hslRegex = /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*(,\s*[\d.]+\s*)?\)$/;
        if (hslRegex.test(color)) return true;

        // Validar nombres de colores CSS válidos
        const validColorNames = [
            'red', 'green', 'blue', 'yellow', 'black', 'white', 'gray', 'grey',
            'orange', 'purple', 'pink', 'brown', 'cyan', 'magenta', 'lime',
            'navy', 'teal', 'olive', 'maroon', 'aqua', 'fuchsia', 'silver'
        ];
        if (validColorNames.includes(color.toLowerCase())) return true;

        return false;
    };

    // Función para normalizar colores a formato hexadecimal
    const normalizeColor = (color) => {
        // Si ya es hexadecimal, devolverlo
        if (color.startsWith('#')) {
            // Expandir colores de 3 dígitos a 6
            if (color.length === 4) {
                return '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3];
            }
            return color;
        }

        // Crear un elemento temporal para convertir el color
        const tempElement = document.createElement('div');
        tempElement.style.color = color;
        document.body.appendChild(tempElement);

        // Obtener el color computado
        const computedColor = window.getComputedStyle(tempElement).color;
        document.body.removeChild(tempElement);

        // Convertir RGB a hexadecimal
        if (computedColor.startsWith('rgb')) {
            const rgb = computedColor.match(/\d+/g);
            if (rgb && rgb.length >= 3) {
                const r = parseInt(rgb[0]).toString(16).padStart(2, '0');
                const g = parseInt(rgb[1]).toString(16).padStart(2, '0');
                const b = parseInt(rgb[2]).toString(16).padStart(2, '0');
                return `#${r}${g}${b}`;
            }
        }

        return color; // Si no se puede convertir, devolver el original
    };

    // Handlers para el formulario
    const handleMarcaChange = (e) => {
        const { name, value } = e.target;
        setNewMarca((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddColor = () => {
        if (!newColor.trim()) {
            Swal.fire({
                title: 'Error',
                text: 'Por favor ingresa un color',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Validar el color
        if (!validateColor(newColor.trim())) {
            Swal.fire({
                title: 'Color Inválido',
                text: 'Por favor ingresa un color válido en formato hexadecimal (#RRGGBB), RGB, HSL o nombre de color CSS',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Normalizar el color
        const normalizedColor = normalizeColor(newColor.trim());

        // Verificar que no esté duplicado
        if (newMarca.coloresCorporativos.includes(normalizedColor)) {
            Swal.fire({
                title: 'Color Duplicado',
                text: 'Este color ya ha sido agregado',
                icon: 'warning',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Agregar el color
        setNewMarca(prev => ({
            ...prev,
            coloresCorporativos: [...prev.coloresCorporativos, normalizedColor]
        }));
        setNewColor('');

        // Mostrar confirmación
        Swal.fire({
            title: 'Color Agregado',
            text: `Color ${normalizedColor} agregado correctamente`,
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
        });
    };

    const handleRemoveColor = (index) => {
        setNewMarca(prev => ({
            ...prev,
            coloresCorporativos: prev.coloresCorporativos.filter((_, i) => i !== index)
        }));
    };

    const handleColorPickerSelect = (color) => {
        setSelectedColor(color);
        setNewColor(color);
    };

    const handleCustomColorChange = (e) => {
        const color = e.target.value;
        setSelectedColor(color);
        setNewColor(color);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Validar que la tipografía esté seleccionada
        if (!newMarca.tipografia || newMarca.tipografia.trim() === '') {
            Swal.fire({
                title: 'Error',
                text: 'Por favor selecciona una tipografía para la marca',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            setIsSubmitting(false);
            return;
        }

        try {
            const marcaData = {
                ...newMarca,
                puntosTicket: parseFloat(newMarca.puntosTicket),
                owner: user.userId,
            };

            // Crear la marca
            const nuevaMarca = await onSubmit(marcaData);
            if (!nuevaMarca) {
                throw new Error('No se pudo crear la marca');
            }
            setMarcaCreada(nuevaMarca);

            // Preguntar si quiere subir el logotipo
            const result = await Swal.fire({
                title: '¡Marca Creada! 🎉',
                text: '¿Quieres subir el logotipo de tu marca ahora?',
                icon: 'success',
                showCancelButton: true,
                confirmButtonText: '¡Sí, subir logo!',
                cancelButtonText: 'Después',
                confirmButtonColor: '#3B82F6',
                cancelButtonColor: '#6B7280'
            });

            if (result.isConfirmed) {
                setShowLogoModal(true);
            } else {
                // Si decide no subir el logo, completamos el proceso y cerramos
                handleClose();
                onComplete();
            }

        } catch (error) {
            console.error('Error creating marca:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudo crear la marca. Por favor, intenta de nuevo.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        } finally {
            setIsSubmitting(false);
        }
    };



    const handleClose = () => {
        // Limpiar el formulario al cerrar
        setNewMarca({
            nombre: '',
            rubro: '',
            tiendas: [],
            clientes: [],
            pointscondition: 'Condición para sumar puntos',
            puntos: 'PUNTOS',
            puntosTicket: 6,
            coloresCorporativos: [],
            tipografia: 'Arial', // Mantener valor por defecto
            eslogan: ''
        });
        setNewColor('');
        setSelectedColor('#3B82F6');
        setShowLogoModal(false);
        setMarcaCreada(null);
        onClose();
    };

    const handleLogoUpload = async (marcaId, file) => {
        try {
            await onUploadLogotipo(marcaId, file);
            Swal.fire({
                title: '¡Logotipo Subido!',
                text: 'El logotipo se ha subido exitosamente',
                icon: 'success',
                confirmButtonText: 'Continuar'
            });
            return true;
        } catch (error) {
            console.error('Error uploading logo:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudo subir el logotipo. Por favor, intenta de nuevo más tarde.',
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
            return false;
        }
    };

    // Modificar la vista previa para usar el mapeo de fuentes
    const getFontFamily = (font) => FONT_FAMILIES[font] || FONT_FAMILIES['Arial'];

    return (
        <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
                {/* Fondo oscuro */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Modal */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl w-full relative">
                    {/* Header */}
                    <div className="bg-white px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                                <FaTags className="mr-2 text-blue-500" />
                                Crear Nueva Marca
                            </h3>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="bg-white px-6 py-4">
                        {/* Formulario */}
                        <form onSubmit={handleSubmit} className="mt-6">
                            <div className="space-y-6">
                                {/* Información básica */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-4 border-b pb-2 flex items-center">
                                        <FaTags className="mr-2 text-blue-500" />
                                        Información Básica
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Nombre de la Marca */}
                                        <div className="mb-4">
                                            <label className="block text-gray-600 text-sm mb-2">
                                                Nombre de la Marca:
                                            </label>
                                            <input
                                                type="text"
                                                name="nombre"
                                                value={newMarca.nombre}
                                                onChange={handleMarcaChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                            />
                                        </div>

                                        {/* Rubro de la Marca */}
                                        <div className="mb-4">
                                            <label className="block text-gray-600 text-sm mb-2">
                                                Rubro de la Marca:
                                            </label>
                                            <select
                                                name="rubro"
                                                value={newMarca.rubro}
                                                onChange={handleMarcaChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                            >
                                                <option value="">Selecciona un rubro</option>
                                                {RUBROS_NEGOCIOS.map((rubro) => (
                                                    <option key={rubro} value={rubro}>{rubro}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Identidad Visual */}
                                <h4 className="text-lg font-medium text-gray-900 mb-4 mt-6 flex items-center">
                                    <FaPalette className="mr-2" />
                                    Identidad Visual
                                </h4>

                                {/* Selector de Colores Mejorado */}
                                <div className="mb-6">
                                    <label className="block text-gray-600 text-sm mb-2">
                                        Colores Corporativos:
                                    </label>

                                    {/* Color picker personalizado */}
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
                                        <p className="text-sm text-gray-600 mb-3">
                                            1. Elige un color predefinido o personalizado:
                                        </p>

                                        {/* Colores predefinidos */}
                                        <div className="grid grid-cols-7 gap-2 mb-4">
                                            {predefinedColors.map((color) => (
                                                <button
                                                    key={color}
                                                    type="button"
                                                    onClick={() => handleColorPickerSelect(color)}
                                                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${selectedColor === color ? 'border-gray-800 ring-2 ring-offset-2 ring-blue-500' : 'border-gray-200'
                                                        }`}
                                                    style={{ backgroundColor: color }}
                                                    title={color}
                                                />
                                            ))}
                                        </div>

                                        {/* Selector personalizado */}
                                        <div className="flex items-center gap-3">
                                            <div className="relative">
                                                <input
                                                    type="color"
                                                    value={selectedColor}
                                                    onChange={handleCustomColorChange}
                                                    className="w-12 h-12 cursor-pointer opacity-0 absolute inset-0"
                                                />
                                                <div
                                                    className="w-12 h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
                                                    style={{ backgroundColor: selectedColor }}
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm text-gray-600">
                                                    2. Color personalizado:
                                                </p>
                                                <p className="text-sm font-medium">
                                                    {selectedColor}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleAddColor}
                                                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                            >
                                                <FaPlus className="w-4 h-4" />
                                                Añadir
                                            </button>
                                        </div>
                                    </div>

                                    {/* Lista de colores seleccionados */}
                                    {newMarca.coloresCorporativos.length > 0 && (
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            <p className="text-sm text-gray-600 mb-3">
                                                Colores seleccionados:
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {newMarca.coloresCorporativos.map((color, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex items-center bg-white rounded-full pl-2 pr-1 py-1 border border-gray-200"
                                                    >
                                                        <div
                                                            className="w-4 h-4 rounded-full mr-2"
                                                            style={{ backgroundColor: color }}
                                                        />
                                                        <span className="text-sm mr-2">{color}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveColor(index)}
                                                            className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                                                        >
                                                            <FaTimes className="w-3 h-3 text-gray-600" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Tipografía */}
                                <div className="mb-6">
                                    <label className="block text-gray-600 text-sm mb-2 flex items-center">
                                        <FaFont className="mr-2" />
                                        Tipografía Principal:
                                    </label>
                                    <div className="relative">
                                        <select
                                            name="tipografia"
                                            value={newMarca.tipografia}
                                            onChange={handleMarcaChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none text-sm"
                                            required
                                            style={{
                                                maxWidth: '100%',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            <option value="" className="text-sm">Selecciona una tipografía</option>

                                            {/* Sans Serif Modernas */}
                                            <optgroup label="Sans Serif Modernas" className="text-sm">
                                                <option value="Poppins" className="text-sm whitespace-normal">Poppins - Moderna y limpia</option>
                                                <option value="Inter" className="text-sm whitespace-normal">Inter - Versátil y legible</option>
                                                <option value="Montserrat" className="text-sm whitespace-normal">Montserrat - Elegante y contemporánea</option>
                                                <option value="Roboto" className="text-sm whitespace-normal">Roboto - Clara y profesional</option>
                                                <option value="Open Sans" className="text-sm whitespace-normal">Open Sans - Amigable y legible</option>
                                                <option value="Lato" className="text-sm whitespace-normal">Lato - Equilibrada y moderna</option>
                                                <option value="Nunito" className="text-sm whitespace-normal">Nunito - Suave y moderna</option>
                                                <option value="DM Sans" className="text-sm whitespace-normal">DM Sans - Minimalista</option>
                                            </optgroup>

                                            {/* Sans Serif Clásicas */}
                                            <optgroup label="Sans Serif Clásicas" className="text-sm">
                                                <option value="Arial" className="text-sm whitespace-normal">Arial - Universal y confiable</option>
                                                <option value="Helvetica" className="text-sm whitespace-normal">Helvetica - Clásica y profesional</option>
                                                <option value="Verdana" className="text-sm whitespace-normal">Verdana - Legible en pantalla</option>
                                                <option value="Tahoma" className="text-sm whitespace-normal">Tahoma - Clara y versátil</option>
                                            </optgroup>

                                            {/* Display y Decorativas */}
                                            <optgroup label="Display y Decorativas" className="text-sm">
                                                <option value="Playfair Display" className="text-sm whitespace-normal">Playfair Display - Elegante y sofisticada</option>
                                                <option value="Quicksand" className="text-sm whitespace-normal">Quicksand - Moderna y geométrica</option>
                                                <option value="Comfortaa" className="text-sm whitespace-normal">Comfortaa - Redondeada y amigable</option>
                                                <option value="Righteous" className="text-sm whitespace-normal">Righteous - Audaz y distintiva</option>
                                            </optgroup>

                                            {/* Serif Modernas */}
                                            <optgroup label="Serif Modernas" className="text-sm">
                                                <option value="Merriweather" className="text-sm whitespace-normal">Merriweather - Elegante y legible</option>
                                                <option value="Source Serif Pro" className="text-sm whitespace-normal">Source Serif Pro - Profesional y refinada</option>
                                                <option value="Crimson Text" className="text-sm whitespace-normal">Crimson Text - Clásica y elegante</option>
                                                <option value="Lora" className="text-sm whitespace-normal">Lora - Contemporánea serif</option>
                                            </optgroup>

                                            {/* Negocios y Comercio */}
                                            <optgroup label="Negocios y Comercio" className="text-sm">
                                                <option value="Work Sans" className="text-sm whitespace-normal">Work Sans - Profesional y moderna</option>
                                                <option value="Karla" className="text-sm whitespace-normal">Karla - Limpia y corporativa</option>
                                                <option value="Manrope" className="text-sm whitespace-normal">Manrope - Tecnológica y moderna</option>
                                                <option value="Outfit" className="text-sm whitespace-normal">Outfit - Versátil y contemporánea</option>
                                            </optgroup>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                    {/* Nota informativa */}
                                    <p className="mt-2 text-xs text-gray-500 italic">
                                        Selecciona la tipografía que mejor represente la personalidad de tu marca
                                    </p>
                                </div>

                                {/* Eslogan */}
                                <div className="mb-6">
                                    <label className="block text-gray-600 text-sm mb-2 flex items-center">
                                        <FaQuoteLeft className="mr-2" />
                                        Eslogan o Tagline (opcional):
                                    </label>
                                    <textarea
                                        name="eslogan"
                                        value={newMarca.eslogan}
                                        onChange={handleMarcaChange}
                                        rows={2}
                                        placeholder="Ej: Innovación que transforma..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                    />
                                </div>

                                {/* Vista previa de la tipografía y eslogan */}
                                {newMarca.tipografia && (
                                    <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                                        <div className="flex items-center mb-4">
                                            <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                                            <p className="text-sm text-blue-600 font-medium">Vista Previa de tu Marca</p>
                                        </div>
                                        <div className="space-y-3">
                                            <p
                                                className="text-2xl font-semibold text-gray-800"
                                                style={{ fontFamily: getFontFamily(newMarca.tipografia) }}
                                            >
                                                {newMarca.nombre || 'Nombre de tu Marca'}
                                            </p>
                                            <p
                                                className="text-lg text-gray-600"
                                                style={{ fontFamily: getFontFamily(newMarca.tipografia) }}
                                            >
                                                {newMarca.eslogan || 'Tu eslogan aparecerá aquí'}
                                            </p>
                                            <div className="flex items-center mt-4 pt-3 border-t border-blue-100">
                                                <FaFont className="text-blue-500 mr-2" />
                                                <p className="text-sm text-blue-500">
                                                    Tipografía: <span className="font-medium">{newMarca.tipografia}</span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Configuración de Fidelización */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-4 border-b pb-2 flex items-center">
                                        <FaTags className="mr-2 text-green-500" />
                                        Configuración de Fidelización
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Condición de Puntos
                                            </label>
                                            <input
                                                type="text"
                                                name="pointscondition"
                                                value={newMarca.pointscondition}
                                                onChange={handleMarcaChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                                placeholder="Condición para sumar puntos"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Tipo de Puntos
                                            </label>
                                            <select
                                                name="puntos"
                                                value={newMarca.puntos}
                                                onChange={handleMarcaChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                            >
                                                <option value="PUNTOS">Puntos</option>
                                                <option value="ACUMULACION">Acumulación</option>
                                            </select>
                                        </div>
                                        {newMarca.puntos === 'PUNTOS' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Puntos por Ticket
                                                </label>
                                                <input
                                                    type="number"
                                                    name="puntosTicket"
                                                    value={newMarca.puntosTicket}
                                                    onChange={handleMarcaChange}
                                                    min={1}
                                                    max={20}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>


                            </div>

                            {/* Botones */}
                            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors duration-200 font-medium flex items-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="w-5 h-5 mr-2 animate-spin" />
                                            Creando...
                                        </>
                                    ) : (
                                        <>
                                            <FaTags className="w-5 h-5 mr-2" />
                                            Crear Marca
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Modal de Logotipo */}
            {showLogoModal && marcaCreada && (
                <LogotipoModal
                    isOpen={showLogoModal}
                    onClose={() => {
                        handleClose();
                        onComplete();
                    }}
                    onUploadLogotipo={async (marcaId, file) => {
                        try {
                            await onUploadLogotipo(marcaId, file);
                            handleClose();
                            onComplete();
                            return true;
                        } catch (error) {
                            console.error('Error uploading logo:', error);
                            return false;
                        }
                    }}
                    marca={marcaCreada}
                    title="Sube el Logotipo de tu Marca"
                    isOnboarding={true}
                />
            )}
        </div>
    );
};



export default CreateMarcaModal; 